import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress, Divider, Grid, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import { useMaterialUIController } from "context";

// Mesmo limite do backend (ModeloBoletoService)
const TAMANHO_MAXIMO_LOGO_KB = 300;
const OPCAO_PADRAO_BANCO = { value: "", label: "Padrão do banco da conta (Itaú: modelo Itaú; demais: padrão)" };
const OPCAO_PADRAO_DOCUMENTO = { value: "", label: "Padrão do layout (Itaú: nº do acordo-parcela; demais: nosso número)" };

/** Modelo do boleto do cliente: layout, saudação e logo do recibo do pagador, e o número do documento. */
export default function ModeloBoletoCliente({ pessoaId }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [opcoes, setOpcoes] = useState([OPCAO_PADRAO_BANCO]);
    const [modelo, setModelo] = useState(OPCAO_PADRAO_BANCO);
    const [saudacao, setSaudacao] = useState("");
    const [opcoesDocumento, setOpcoesDocumento] = useState([OPCAO_PADRAO_DOCUMENTO]);
    const [numeroDocumento, setNumeroDocumento] = useState(OPCAO_PADRAO_DOCUMENTO);
    const [logo, setLogo] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const inputLogo = useRef(null);

    useEffect(() => {
        if (!pessoaId) return;
        setCarregando(true);
        Promise.all([get("/modelo-boleto/opcoes"), get(`/modelo-boleto/cliente/${pessoaId}`), get("/modelo-boleto/opcoes-numero-documento")])
            .then(([listaOpcoes, config, listaDocumento]) => {
                const todas = [OPCAO_PADRAO_BANCO, ...(listaOpcoes || [])];
                setOpcoes(todas);
                setModelo(todas.find(o => o.value === (config?.modeloBoleto || "")) || OPCAO_PADRAO_BANCO);
                const todasDocumento = [OPCAO_PADRAO_DOCUMENTO, ...(listaDocumento || [])];
                setOpcoesDocumento(todasDocumento);
                setNumeroDocumento(todasDocumento.find(o => o.value === (config?.numeroDocumento || "")) || OPCAO_PADRAO_DOCUMENTO);
                setSaudacao(config?.saudacao || "");
                setLogo(config?.logo || null);
            })
            .catch(() => errorAlert("Erro ao carregar o modelo do boleto do cliente."))
            .finally(() => setCarregando(false));
    }, [pessoaId]);

    const handleArquivoLogo = (event) => {
        const arquivo = event.target.files?.[0];
        event.target.value = "";
        if (!arquivo) return;
        if (!["image/png", "image/jpeg"].includes(arquivo.type)) {
            errorAlert("O logo deve ser uma imagem PNG ou JPG.");
            return;
        }
        if (arquivo.size > TAMANHO_MAXIMO_LOGO_KB * 1024) {
            errorAlert(`O logo pode ter no máximo ${TAMANHO_MAXIMO_LOGO_KB} KB.`);
            return;
        }
        const leitor = new FileReader();
        leitor.onload = () => setLogo(leitor.result);
        leitor.readAsDataURL(arquivo);
    };

    const handleSalvar = () => {
        setSalvando(true);
        postNoResponse("/modelo-boleto/cliente/save", {
            idPessoa: pessoaId,
            modeloBoleto: modelo?.value || "",
            saudacao,
            logo: logo || "",
            numeroDocumento: numeroDocumento?.value || "",
        })
            .then(() => successAlert("Modelo do boleto salvo!"))
            .catch((err) => errorAlert(err?.message || "Erro ao salvar o modelo do boleto."))
            .finally(() => setSalvando(false));
    };

    return (
        <MDBox>
            <Divider sx={{ my: 0 }} />
            <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>Modelo do Boleto</MDTypography>
                {carregando ? (
                    <MDBox display="flex" justifyContent="center" py={3}><CircularProgress size={28} color="info" /></MDBox>
                ) : (
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} md={5}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.5, display: "block" }}>Layout</MDTypography>
                            <MDSelect
                                isMulti={false}
                                name="modeloBoleto"
                                value={modelo}
                                options={opcoes}
                                getOptionValue={o => o.value}
                                getOptionLabel={o => o.label}
                                onChange={val => setModelo(val || OPCAO_PADRAO_BANCO)}
                            />
                            <MDTypography variant="caption" color="text" sx={{ px: 1, pt: 0.5, display: "block" }}>
                                O modelo Itaú tem recibo do pagador com logo e saudação, e até 5 linhas de instrução.
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.5, display: "block" }}>Saudação</MDTypography>
                            <MDInput
                                fullWidth
                                value={saudacao}
                                placeholder="Ex: Prezado aluno(a)"
                                inputProps={{ maxLength: 100 }}
                                onChange={e => setSaudacao(e.target.value)}
                            />
                            <MDTypography variant="caption" color="text" sx={{ px: 1, pt: 0.5, display: "block" }}>
                                Impressa antes do nome do pagador, no topo do recibo. Em branco, não aparece.
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.5, display: "block" }}>Logo</MDTypography>
                            <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    height: 64,
                                    borderRadius: 1,
                                    border: `1px dashed ${darkMode ? "rgba(255,255,255,0.3)" : "#d2d6da"}`,
                                    background: "#fff",
                                    overflow: "hidden",
                                }}
                            >
                                {logo
                                    ? <img src={logo} alt="Logo do boleto" style={{ maxHeight: 58, maxWidth: "100%" }} />
                                    : <MDTypography variant="caption" sx={{ color: "#7b809a" }}>Sem logo</MDTypography>}
                            </MDBox>
                            <input ref={inputLogo} type="file" accept="image/png,image/jpeg" hidden onChange={handleArquivoLogo} />
                            <MDBox display="flex" gap={1} mt={1}>
                                <MDButton variant="outlined" color="info" size="small" onClick={() => inputLogo.current?.click()}>
                                    <Icon sx={{ mr: 0.5 }}>upload</Icon>{logo ? "Trocar" : "Selecionar"}
                                </MDButton>
                                {logo && (
                                    <MDButton variant="outlined" color="error" size="small" onClick={() => setLogo(null)}>
                                        Remover
                                    </MDButton>
                                )}
                            </MDBox>
                            <MDTypography variant="caption" color="text" sx={{ px: 1, pt: 0.5, display: "block" }}>
                                PNG ou JPG, até {TAMANHO_MAXIMO_LOGO_KB} KB.
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ px: 1, pb: 0.5, display: "block" }}>Número do documento</MDTypography>
                            <MDSelect
                                isMulti={false}
                                name="numeroDocumento"
                                value={numeroDocumento}
                                options={opcoesDocumento}
                                getOptionValue={o => o.value}
                                getOptionLabel={o => o.label}
                                onChange={val => setNumeroDocumento(val || OPCAO_PADRAO_DOCUMENTO)}
                            />
                            <MDTypography variant="caption" color="text" sx={{ px: 1, pt: 0.5, display: "block" }}>
                                O que sai no campo "Número do documento" do boleto. Código do cliente: o do devedor (em branco, fica o padrão).
                                A remessa ao banco não muda.
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <MDButton variant="gradient" color="info" size="small" onClick={handleSalvar} disabled={salvando}>
                                {salvando ? <CircularProgress size={16} color="inherit" /> : "Salvar modelo do boleto"}
                            </MDButton>
                        </Grid>
                    </Grid>
                )}
            </MDBox>
        </MDBox>
    );
}

ModeloBoletoCliente.propTypes = {
    pessoaId: PropTypes.string.isRequired,
};
